import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, Cpu, Users, Award, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SectionHeader } from '../components/common/SectionHeader';
import { CTASection } from '../components/common/CTASection';
import { LogoCarousel } from '../components/common/LogoCarousel';
import { SectorsCarousel } from '../components/common/SectorsCarousel';
import { TestimonialsCarousel } from '../components/common/TestimonialsCarousel';
import { ServiceCard } from '../components/common/ServiceCard';
import { HeroDecoration, ParticleField } from '../components/decorations/GeometricBackground';
import { companyInfo, clientSectors } from '../data/company';
import { services } from '../data/services';
import { partnershipsData } from '../data/partnerships';
import { certificationsData } from '../data/certifications';
import { manufacturers } from '../data/products';

const HomePage = () => {
  const renewableService = services.find(s => s.id === 'renewable-energy');
  const coreServices = services.filter(s => s.id !== 'renewable-energy');

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-hero-pattern overflow-hidden">
        <HeroDecoration />
        <ParticleField count={40} />
        
        <div className="container-custom relative z-10 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <Badge className="mb-6 bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20">
                ICT Solutions Provider
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-primary-foreground leading-tight mb-6">
                {companyInfo.tagline.split(', ').map((part, i) => (
                  <span key={i} className="block">
                    {part}{i === 0 ? ',' : ''}
                  </span>
                ))}
              </h1>
              
              <p className="text-lg md:text-xl text-primary-foreground/80 mb-8 max-w-xl mx-auto lg:mx-0">
                {companyInfo.heroQuote}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button asChild variant="hero-accent" size="xl">
                  <Link to="/services">
                    Explore Services
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="hero-outline" size="xl" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  <Link to="/contact">
                    Talk to an Expert
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Hero Image/Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800&auto=format&fit=crop&q=80"
                  alt="African professionals working with technology"
                  className="rounded-2xl shadow-2xl"
                />
                {/* Stats overlay */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="absolute -bottom-6 -left-6 bg-card rounded-xl p-4 shadow-xl border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Award className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">40+</p>
                      <p className="text-sm text-muted-foreground">Years Combined Expertise</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TaaS / About Preview */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4">Technology as a Service</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Comprehensive ICT Solutions Tailored to Your Needs
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {companyInfo.about.taas}
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {companyInfo.strengths.slice(0, 4).map((strength, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{strength}</span>
                  </div>
                ))}
              </div>
              <Button asChild variant="default">
                <Link to="/about">
                  Learn More About Us
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/img/header/comprehensive.png"
                alt="Team collaboration"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            subtitle="Our Services"
            title="Comprehensive ICT Solutions"
            description="From unified communications to cybersecurity, we deliver end-to-end technology services that drive business success."
            className="mb-16"
          />
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>



      {/* Testimonials */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <SectionHeader
            subtitle="Client Testimonials"
            title="What Our Clients Say"
            description="Hear from organizations that have transformed their operations with Voitech Solutions."
            className="mb-12"
          />
          <TestimonialsCarousel />
        </div>
      </section>

      {/* Renewable Energy Teaser */}
      {/* {renewableService && (
        <section className="section-padding bg-card">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Badge className="mb-4 bg-accent/10 text-accent">Renewable Energy</Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  {renewableService.detailBlock.heading}
                </h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {renewableService.detailBlock.content}
                </p>
                <h4 className="font-semibold mb-3">Typical Use Cases:</h4>
                <ul className="space-y-2 mb-8">
                  {renewableService.useCases.map((useCase, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{useCase}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild variant="accent">
                  <Link to="/services/renewable-energy">
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <img
                  src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&auto=format&fit=crop&q=80"
                  alt="Solar panels powering ICT infrastructure"
                  className="rounded-2xl shadow-xl"
                />
              </motion.div>
            </div>
          </div>
        </section>
      )} */}

      {/* Partnerships Preview */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            subtitle="Strategic Partnerships"
            title="Trusted by Leading Brands"
            description="We partner with globally renowned technology companies to deliver best-in-class solutions."
            className="mb-12"
          />
          <LogoCarousel logos={partnershipsData.partners.slice(0, 15)} />
          <div className="text-center mt-8">
            <Button asChild variant="outline">
              <Link to="/partnerships">View All Partners</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Certifications Preview */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <SectionHeader
            subtitle="Certified Excellence"
            title="Industry-Recognized Certifications"
            description={certificationsData.statement}
            className="mb-12"
          />
          <LogoCarousel logos={certificationsData.certifications} />
          <div className="text-center mt-8">
            <Button asChild variant="outline">
              <Link to="/certifications">View All Certifications</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Products Teaser */}
      {/* <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            subtitle="Our Products"
            title="Products We Supply"
            description="We supply trusted ICT and security equipment—from networking and unified communications to power, servers, and surveillance."
            className="mb-12"
          />
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {manufacturers.slice(0, 12).map((manufacturer) => (
              <div
                key={manufacturer}
                className="px-6 py-3 bg-card rounded-lg border border-border/50 text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/30 transition-colors"
              >
                {manufacturer}
              </div>
            ))}
          </div>
          <div className="text-center">
            <Button asChild>
              <Link to="/products">View Products</Link>
            </Button>
          </div>
        </div>
      </section> */}

      {/* Valued Clients Preview */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <SectionHeader
            subtitle="Valued Clients"
            title="Industries We Serve"
            description="Our tailored solutions are designed and deployed across diverse sectors throughout Uganda and beyond."
            className="mb-12"
          />
          <SectorsCarousel sectors={clientSectors} />
          <div className="text-center mt-8">
            <Button asChild variant="outline">
              <Link to="/clients">Learn More</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <CTASection
        title="Ready to Secure and Modernize Your ICT?"
        description="Let's discuss how Voitech Solutions can help transform your technology infrastructure and empower your organization."
        primaryButtonText="Request Consultation"
        primaryButtonLink="/contact"
        variant="gradient"
      />
    </div>
  );
};

export default HomePage;
